import { ReactComponent as Ic3Points } from '@assets/components/panel/ic-3points-h18.svg';
import { IcCopyPaste, IcEdit, IcPlus as IcPlus24, IcTrash } from '@assets/icons/24x24';
import { IcPlus as IcPlus32 } from '@assets/icons/32x32';
import { ButtonGroup, Center, For, HStack, Icon, IconButton, Text, VStack } from '@chakra-ui/react';
import CockpitDialog, { CockpitDialogProps } from '@components/cockpit/CockpitDialog';
import { CockpitListLayout } from '@components/cockpit/layouts/CockpitListLayout';
import CockpitRow from '@components/cockpit/layouts/CockpitRow';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { Tooltip } from '@components/ui/tooltip';
import { useAppSelector } from '@hooks/redux.hooks';
import useListFormatter from '@hooks/useListFormatter';
import { hasRole, selectAllGroups } from '@redux/authent/authent.selectors';
import { GlobalScreenConfigurationSelectors, PlatformSelectors } from '@redux/config/config.selectors';
import { useCreateGroupMutation, useDeleteGroupMutation } from '@services/authent/group.api';
import { isRestrictedGroup } from '@utils/authent/profiles.utils';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { NormalizedGroup, Profile } from '@/types/authent/groups.types';
import { RoleEnum } from '@/types/authent/roles.types';

import { UsersManagementItemTypeEnum, useUsersManagementContext } from '../UsersManagement';

const MAX_PLATFORM = 3;

type GroupsDialogProps = {
  usersLength?: number;
  selectedGroupName?: string;
} & Omit<CockpitDialogProps, 'children'>;

function GroupsDialog({
  open,
  usersLength,
  selectedGroupName,
  isLoading,
  onConfirm,
  onCancel,
}: Readonly<GroupsDialogProps>) {
  return (
    <CockpitDialog open={open} isLoading={isLoading} onCancel={onCancel} onConfirm={onConfirm}>
      <Text color="neutral.100" width="100%" whiteSpace="pre-line">
        <FormattedMessage
          id="components.usersManagement.groups.popup.descriptionDelete"
          values={{ name: selectedGroupName }}
        />
      </Text>
      <Text color="neutral.100" width="100%" whiteSpace="pre-line">
        <FormattedMessage id="components.usersManagement.groups.popup.descriptionDeleteLogout" />
      </Text>
      {usersLength !== 0 && (
        <Text color="neutral.100" width="100%" whiteSpace="pre-line">
          <FormattedMessage id="components.usersManagement.groups.popup.descriptionDeleteWarning" />
        </Text>
      )}
    </CockpitDialog>
  );
}

type GroupsActionsProps = {
  canCreateGroup: boolean;
  groupsLength: number;
  onCreate: () => void;
};

function GroupsActions({ canCreateGroup, groupsLength, onCreate }: Readonly<GroupsActionsProps>) {
  return (
    <>
      <Text color="neutral.500">
        <FormattedMessage id="components.usersManagement.groups.result" values={{ count: groupsLength }} />
      </Text>
      {canCreateGroup && (
        <IconButton aria-label="Add" variant="transparent" boxSize="56px" marginLeft="auto" onClick={onCreate}>
          <Icon as={IcPlus24} boxSize="24px" />
        </IconButton>
      )}
    </>
  );
}

function GroupsHeader() {
  return (
    <>
      <Text width="176px" color="neutral.500">
        <FormattedMessage id="components.usersManagement.groups.name" />
      </Text>
      <Text width="240px" color="neutral.500">
        <FormattedMessage id="components.usersManagement.groups.platforms" />
      </Text>
      <Text width="176px" color="neutral.500">
        <FormattedMessage id="components.usersManagement.groups.config" />
      </Text>
      <Text width="176px" color="neutral.500">
        <FormattedMessage id="components.usersManagement.groups.profile" />
      </Text>
      <Text width="176px" color="neutral.500">
        <FormattedMessage id="components.usersManagement.groups.ldapGroupDn" />
      </Text>
      <Text width="104px" color="neutral.500">
        <FormattedMessage id="components.usersManagement.groups.inactivityTime" />
      </Text>
    </>
  );
}

export default function Groups() {
  const { formatMessage } = useIntl();
  const formatList = useListFormatter();
  const groups = useAppSelector(selectAllGroups);
  const globalScreenConfigurations = useAppSelector(
    GlobalScreenConfigurationSelectors.selectAllGlobalScreenConfigurations,
  );
  const platforms = useAppSelector(PlatformSelectors.selectAllPlatforms);
  const [selectedGroup, setSelectedGroup] = useState<NormalizedGroup | null>(null);
  const [deleteGroup, { isLoading }] = useDeleteGroupMutation();
  const [createGroup] = useCreateGroupMutation();
  const [loadingCopy, setLoadingCopy] = useState<number | null>(null);
  const canEditGroup = useAppSelector((state) => hasRole(state, RoleEnum.EDIT_GROUP));
  const canCreateGroup = useAppSelector((state) => hasRole(state, RoleEnum.CREATE_GROUP));
  const canDeleteGroup = useAppSelector((state) => hasRole(state, RoleEnum.DELETE_GROUP));

  const { setEditedItem } = useUsersManagementContext();

  return (
    <CockpitListLayout
      dialog={
        <GroupsDialog
          open={selectedGroup !== null}
          usersLength={selectedGroup?.users.length}
          selectedGroupName={selectedGroup?.name}
          isLoading={isLoading}
          onCancel={() => setSelectedGroup(null)}
          onConfirm={() => {
            if (!selectedGroup) {
              return;
            }
            deleteGroup(selectedGroup.id)
              .unwrap()
              .then(() => {
                createToast(
                  formatMessage(
                    { id: 'components.usersManagement.groups.message.delete' },
                    { name: selectedGroup.name },
                  ),
                  ToastStatusEnum.SUCCESS,
                );
                setSelectedGroup(null);
              });
          }}
        />
      }
      actions={
        <GroupsActions
          canCreateGroup={canCreateGroup}
          groupsLength={groups.length}
          onCreate={() => setEditedItem({ type: UsersManagementItemTypeEnum.GROUP, id: null })}
        />
      }
      header={groups.length > 0 && <GroupsHeader />}
    >
      <For
        each={groups}
        fallback={
          canCreateGroup ? (
            <VStack
              padding={4}
              gap={2}
              marginY="auto"
              className="group"
              border="2px solid"
              cursor="pointer"
              borderColor="neutral.600"
              _hover={{ borderColor: 'skyBlue', backgroundColor: 'neutral.800' }}
              onClick={() => setEditedItem({ type: UsersManagementItemTypeEnum.GROUP, id: null })}
            >
              <Icon as={IcPlus32} boxSize="32px" color="neutral.300" _groupHover={{ color: 'skyBlue' }} />
              <Text>
                <FormattedMessage id="components.usersManagement.groups.createFirst" />
              </Text>
            </VStack>
          ) : undefined
        }
      >
        {(group, index) => {
          const groupPlatforms = platforms.filter((platform) => group.platforms.includes(platform.code));
          const globalConfigurationName = globalScreenConfigurations.find(
            (config) => group.globalScreenConfig === config.code,
          )?.name;
          const isRestricted = isRestrictedGroup(group);
          return (
            <CockpitRow index={index} listLength={groups.length} key={index}>
              <Tooltip content={group.name} openDelay={500}>
                <Text width="176px" lineClamp={1}>
                  {group.name}
                </Text>
              </Tooltip>
              {groupPlatforms.length > 0 ? (
                <HStack width="240px" gap={0.5}>
                  <For each={groupPlatforms.slice(0, MAX_PLATFORM)}>
                    {(platform) => (
                      <Tooltip key={platform.code} content={platform.name}>
                        <Text
                          padding={1}
                          backgroundColor="neutral.900"
                          textTransform="uppercase"
                          lineHeight="18px"
                          userSelect="none"
                        >
                          {platform.name.slice(0, 4)}
                        </Text>
                      </Tooltip>
                    )}
                  </For>
                  {groupPlatforms.length > MAX_PLATFORM && (
                    <Tooltip content={formatList(groupPlatforms.slice(MAX_PLATFORM).map((platform) => platform.name))}>
                      <Center padding={1} backgroundColor="neutral.900">
                        <Icon as={Ic3Points} color="neutral.300" />
                      </Center>
                    </Tooltip>
                  )}
                </HStack>
              ) : (
                <Text width="240px" lineClamp={1}>
                  <FormattedMessage id="global.noInformation" />
                </Text>
              )}
              <Tooltip content={globalConfigurationName} disabled={!globalConfigurationName} openDelay={500}>
                <Text width="176px" lineClamp={1}>
                  {globalConfigurationName ?? formatMessage({ id: 'global.noInformation' })}
                </Text>
              </Tooltip>
              <Tooltip content={group.profileName} openDelay={500} disabled={group.profile === null}>
                <Text width="176px" lineClamp={1}>
                  {group.profileName ?? <FormattedMessage id="global.noInformation" />}
                </Text>
              </Tooltip>
              <Tooltip content={group.ldapGroupDn} openDelay={500} disabled={group.ldapGroupDn === null}>
                <Text width="176px" lineClamp={1}>
                  {group.ldapGroupDn ?? <FormattedMessage id="global.noInformation" />}
                </Text>
              </Tooltip>
              <Text width="104px">{group.inactivityTime}</Text>
              <ButtonGroup marginLeft="auto" attached height="100%">
                {canEditGroup && (
                  <IconButton
                    variant="transparent"
                    boxSize="56px"
                    aria-label="edit"
                    onClick={() => setEditedItem({ type: UsersManagementItemTypeEnum.GROUP, id: group.code })}
                  >
                    <Icon as={IcEdit} boxSize="24px" />
                  </IconButton>
                )}
                {canCreateGroup && !isRestricted && (
                  <IconButton
                    variant="transparent"
                    boxSize="56px"
                    aria-label="duplicate"
                    loading={loadingCopy === group.id}
                    onClick={() => {
                      setLoadingCopy(group.id);
                      createGroup({
                        ...group,
                        id: undefined,
                        code: uuidv4(),
                        name: `${group.name}-copy`,
                        users: [],
                        profile: group.profile ? ({ id: Number(group.profile) } as Profile) : null,
                      })
                        .unwrap()
                        .then(() => {
                          setLoadingCopy(null);
                          createToast(
                            formatMessage(
                              { id: 'components.usersManagement.groups.message.copy' },
                              { name: group.name },
                            ),
                            ToastStatusEnum.SUCCESS,
                          );
                        });
                    }}
                  >
                    <Icon as={IcCopyPaste} boxSize="24px" />
                  </IconButton>
                )}
                {canDeleteGroup && !isRestricted && (
                  <IconButton
                    variant="transparent"
                    boxSize="56px"
                    aria-label="delete"
                    onClick={() => setSelectedGroup(group)}
                  >
                    <Icon as={IcTrash} boxSize="24px" />
                  </IconButton>
                )}
              </ButtonGroup>
            </CockpitRow>
          );
        }}
      </For>
    </CockpitListLayout>
  );
}
